import { Component, EventEmitter, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { MultiSessionService, SessionEntry } from '../../../services/multi-session.service'
import { NotificationService } from '../../../services'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-modal-add-login',
  templateUrl: './modal-add-login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalAddLoginComponent {
  @ViewChild('modal') modal: ModalContainerComponent
  @Output() accountAdded = new EventEmitter<SessionEntry>()

  private get defaultClientType(): string {
    return window.location.href.includes('local') || window.location.href.includes('stage') ? 'stage' : 'prod'
  }

  clientTypeOptions = [
    { id: 'prod', name: 'production' },
    { id: 'stage', name: 'testing' },
    { id: 'custom', name: 'custom' },
  ]

  form: FormGroup = this.buildForm()

  loading = false
  hasError = false
  submitted = false
  showPassword = false
  // The form is CSS-hidden (not removed) while the modal is closed, since ModalContainerComponent
  // only toggles display via ngx-bootstrap's bsModal directive. Chrome's autofill agent only
  // classifies a <form> as an eligible sign-in form on initial page load or on DOM mutation, not
  // on a visibility/class change, so a form that merely becomes visible never gets the saved-
  // credentials dropdown the login page's form gets. Tearing it down and re-inserting it fresh on
  // every open gives Chrome the mutation event it needs to (re)classify the form.
  isFormRendered = false
  // Set when the modal is opened to re-authenticate an existing (expired) session rather than
  // add a brand new one — locks email/server so the login overwrites that session's token
  // instead of accidentally creating a second entry for a mistyped email.
  isReauth = false

  constructor(
    private multiSession: MultiSessionService,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {}

  private buildForm(): FormGroup {
    return new FormGroup({
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required]),
      clientType: new FormControl(this.defaultClientType, [Validators.required]),
      customUrl: new FormControl(''),
    })
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.form.controls
  }

  async addAccount(): Promise<void> {
    this.submitted = true
    if (this.form.invalid) return

    this.loading = true
    this.hasError = false

    const { email, password, clientType, customUrl } = this.form.value
    const entry = await this.multiSession.addAccountLogin(email, password, clientType, customUrl || undefined)

    this.loading = false
    if (entry) {
      this.accountAdded.emit(entry)
      this.hideChildModal()
    } else {
      this.hasError = true
    }
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword
  }

  showChildModal(reauth?: { email: string; clientType: string; clientCustomUri?: string }): void {
    this.isReauth = !!reauth
    this.form.setValue({
      email: reauth?.email ?? '',
      password: '',
      clientType: reauth?.clientType ?? this.defaultClientType,
      customUrl: reauth?.clientCustomUri ?? '',
    })
    this.submitted = false
    this.hasError = false
    this.showPassword = false
    this.isFormRendered = false
    setTimeout(() => {
      this.isFormRendered = true
      this.modal.showChildModal()
    })
  }

  hideChildModal(): void {
    this.modal.hideChildModal()
  }
}
